// A birthday is stored as a timestamp at midnight of that day in Iran, which is what an Iranian browser has always
// written (the old code took the browser's own midnight). Reading or writing it with the browser's time zone moved the
// day by one for a visitor outside Iran, so these helpers work on Iran's calendar day (UTC+3:30) whatever the browser's
// zone is. For a browser in Iran they give the same values as before. Plain Jalali arithmetic (the jalaali algorithm),
// no imports, so it runs in the browser, on the server and under `node --test`.

const DAY_MS = 24 * 60 * 60 * 1000;
const IRAN_OFFSET_MS = 3.5 * 60 * 60 * 1000;

const div = (a, b) => ~~(a / b);
const mod = (a, b) => a - ~~(a / b) * b;

const BREAKS = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];

const jal_cal = (jy) => {
	const gy = jy + 621;
	let leap_j = -14;
	let jp = BREAKS[0];
	let jump = 0;
	for (let i = 1; i < BREAKS.length; i += 1) {
		const jm = BREAKS[i];
		jump = jm - jp;
		if (jy < jm) break;
		leap_j = leap_j + div(jump, 33) * 8 + div(mod(jump, 33), 4);
		jp = jm;
	}
	let n = jy - jp;
	leap_j = leap_j + div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
	if (mod(jump, 33) === 4 && jump - n === 4) leap_j += 1;
	const leap_g = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
	const march = 20 + leap_j - leap_g;
	if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
	let leap = mod(mod(n + 1, 33) - 1, 4);
	if (leap === -1) leap = 4;
	return { leap, gy, march };
};

const g2d = (gy, gm, gd) => {
	let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408;
	d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
	return d;
};

const d2g = (jdn) => {
	let j = 4 * jdn + 139361631;
	j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
	const i = div(mod(j, 1461), 4) * 5 + 308;
	const gd = div(mod(i, 153), 5) + 1;
	const gm = mod(div(i, 153), 12) + 1;
	const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
	return { gy, gm, gd };
};

const j2d = (jy, jm, jd) => {
	const r = jal_cal(jy);
	return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
};

const d2j = (jdn) => {
	const { gy } = d2g(jdn);
	let jy = gy - 621;
	const r = jal_cal(jy);
	let k = jdn - g2d(gy, 3, r.march);
	if (k >= 0) {
		if (k <= 185) return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
		k -= 186;
	} else {
		jy -= 1;
		k += 179;
		if (r.leap === 1) k += 1;
	}
	return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
};

/** Jalali date -> Gregorian `{ gy, gm, gd }`. */
export const jalali_to_gregorian = (jy, jm, jd) => d2g(j2d(jy, jm, jd));

/** Gregorian date -> Jalali `{ jy, jm, jd }`. */
export const gregorian_to_jalali = (gy, gm, gd) => d2j(g2d(gy, gm, gd));

/**
 * Stored birthday -> its Jalali day as strings `{ year, month, day }` ("1370", "5", "9", like the old
 * `jalaliDateToString(...).split("/")`), or null. The timestamp is rounded to the nearest midnight in Iran, which also
 * reads records written under Iran's former summer time (+4:30) or by a browser elsewhere as the intended day.
 */
export const birthday_to_jalali_parts = (timestamp) => {
	const ts = typeof timestamp === "number" ? timestamp : parseInt(timestamp, 10);
	if (!Number.isFinite(ts)) return null;
	const day = new Date(Math.round((ts + IRAN_OFFSET_MS) / DAY_MS) * DAY_MS);
	const { jy, jm, jd } = gregorian_to_jalali(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate());
	return { year: String(jy), month: String(jm), day: String(jd) };
};

/** Jalali birthday -> the timestamp stored for it (midnight of that day in Iran), or null when a part is not a number. */
export const jalali_birthday_to_timestamp = (year, month, day) => {
	const [jy, jm, jd] = [year, month, day].map((value) => parseInt(value, 10));
	if (![jy, jm, jd].every(Number.isFinite)) return null;
	const { gy, gm, gd } = jalali_to_gregorian(jy, jm, jd);
	return Date.UTC(gy, gm - 1, gd) - IRAN_OFFSET_MS;
};

// ---------- Calendar-aware birthday helpers ----------
// From chawk-bazar and ciseco `helpers/accountInformation.js` (get_calendar, timestamp_to_parts, parts_to_timestamp,
// validate_birthday, get_birthday_years, get_month_names, format_birthday): Persian calendar for fa, Gregorian for
// en/ar, parts as numbers. Reading rounds to the nearest Iran midnight like `birthday_to_jalali_parts` (the site copies
// floored, which moved records written under the old +4:30 summer time back a day).

/** How many years `get_birthday_years` lists before the current one. */
export const BIRTHDAY_YEARS_BACK = 100;

/** Calendar for a locale: "persian" for fa, "gregory" for every other locale (en, ar). */
export const get_birthday_calendar = (locale) => (locale === "fa" ? "persian" : "gregory");

// Stored value -> number, or null for null / undefined / "" / 0 (0 is the backend's empty value) / not a number.
const to_birthday_timestamp = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const number = Number(value);
	return Number.isFinite(number) && number !== 0 ? number : null;
};

// UTC midnight of the Iran day: `round` for stored birthdays, `floor` for "now".
const iran_day_utc = (ts, how = Math.round) => how((ts + IRAN_OFFSET_MS) / DAY_MS) * DAY_MS;

const utc_day_to_parts = (utc_day, calendar) => {
	const date = new Date(utc_day);
	const gy = date.getUTCFullYear();
	const gm = date.getUTCMonth() + 1;
	const gd = date.getUTCDate();
	if (calendar !== "persian") return { year: gy, month: gm, day: gd };
	const { jy, jm, jd } = gregorian_to_jalali(gy, gm, gd);
	return { year: jy, month: jm, day: jd };
};

// Calendar day -> its UTC midnight, or null when that day does not exist (31 Shahrivar, 30 Esfand of a common year, 30 Feb).
const parts_to_utc_day = ({ year, month, day }, calendar) => {
	let gy = year;
	let gm = month;
	let gd = day;
	if (calendar === "persian") {
		({ gy, gm, gd } = jalali_to_gregorian(year, month, day));
	}
	const date = new Date(0);
	date.setUTCFullYear(gy, gm - 1, gd);
	const utc_day = date.getTime();
	const back = utc_day_to_parts(utc_day, calendar);
	return back.year === year && back.month === month && back.day === day ? utc_day : null;
};

/** Stored birthday -> `{ year, month, day }` as numbers in `calendar` ("persian" | "gregory"), or null. */
export const birthday_to_parts = (timestamp, calendar = "persian") => {
	const value = to_birthday_timestamp(timestamp);
	if (value === null) return null;
	return utc_day_to_parts(iran_day_utc(value), calendar);
};

/** `{ year, month, day }` (numbers or numeric strings) in `calendar` -> the stored timestamp (midnight in Iran), or null for a day that does not exist. */
export const birthday_parts_to_timestamp = (parts, calendar = "persian") => {
	const year = Number(parts?.year);
	const month = Number(parts?.month);
	const day = Number(parts?.day);
	if (![year, month, day].every(Number.isInteger) || month < 1 || month > 12 || day < 1 || day > 31 || year < 1) return null;
	const utc_day = parts_to_utc_day({ year, month, day }, calendar);
	return utc_day === null ? null : utc_day - IRAN_OFFSET_MS;
};

/**
 * Birthday form check -> `{ code, birthday }`. `code` is null, "birthday_invalid" (missing part or a day that does not
 * exist) or "birthday_future"; `birthday` is the timestamp to save when `code` is null. `now` is for tests.
 */
export const validate_birthday_parts = (parts, calendar = "persian", now = Date.now()) => {
	const timestamp = birthday_parts_to_timestamp(parts, calendar);
	if (timestamp === null) return { code: "birthday_invalid", birthday: null };
	if (timestamp > now) return { code: "birthday_future", birthday: null };
	return { code: null, birthday: timestamp };
};

/** Years for the year select, newest first: this year (today in Iran, in `calendar`) back `years_back` years. */
export const get_birthday_years = (calendar = "persian", now = Date.now(), years_back = BIRTHDAY_YEARS_BACK) => {
	if (!Number.isFinite(now)) return [];
	const current = utc_day_to_parts(iran_day_utc(now, Math.floor), calendar).year;
	return Array.from({ length: years_back + 1 }, (_, index) => current - index);
};

/** The 12 month names for `locale` in `calendar` (Intl, Latin digits), index 0 = month 1. */
export const get_birthday_month_names = (locale = "fa", calendar = get_birthday_calendar(locale)) => {
	const formatter = new Intl.DateTimeFormat(`${locale}-u-ca-${calendar}-nu-latn`, { month: "long", timeZone: "UTC" });
	return Array.from({ length: 12 }, (_, index) => {
		// the middle of each month of any year, so a one-day calendar difference cannot pick the wrong month
		const utc_day = parts_to_utc_day({ year: calendar === "persian" ? 1400 : 2000, month: index + 1, day: 15 }, calendar);
		return utc_day === null ? String(index + 1) : formatter.format(new Date(utc_day));
	});
};

/** Birthday for display ("11 دی 1369", "January 1, 1991", "1 يناير 1991") in the locale's calendar; nothing stored -> "". */
export const format_birthday = (timestamp, locale = "fa", { calendar = get_birthday_calendar(locale), date_style = "long" } = {}) => {
	const value = to_birthday_timestamp(timestamp);
	if (value === null) return "";
	return new Intl.DateTimeFormat(`${locale}-u-ca-${calendar}-nu-latn`, { dateStyle: date_style, timeZone: "UTC" }).format(new Date(iran_day_utc(value)));
};
